import { useMemo } from "react";
import MDBox from "components/MDBox";
import Card from "@mui/material/Card";
import { useTheme } from "@mui/material/styles";

import defaultLogo from "assets/images/logos/HeaderLogo.svg";
import leumitLogo from "assets/images/logos/LeumitLogo.svg";
import AttentionIcon from "assets/Icons/attention.svg?react";
import { makeStyles } from "tss-react/mui";
import { styles } from "./Header.styles";
import SelectLanguage from "components/SelectLanguage/SelectLaguage";

import { useTranslation } from "react-i18next";
import classNames from "classnames";
import ProgressWidget from "components/ProgressWidget/ProgressWidget";
import { useAppSelector } from "store/hooks";
import { getTestTypeValue } from "store/slices/test";
import { getTestTypeLocaleKey } from "utils/testType.utils";
import { detectHmo } from "utils/hmoDetection";
import { HMO, isSelfReportHmo } from "constants/hmo";
import packageJson from "../../../../../package.json";

interface HeaderProps {
  currentStep: number;
}

type descriptionType = "TEXT" | "CONTAINER" | "STEPS" | "LARGE_TEXT" | "SHORT";

interface StepsHeaderContent {
  [key: number]: {
    descriptionType: descriptionType;
    logo: string;
    title?: string;
    description?: string[];
    stepWidgetProgress?: number;
  };
}

const VERSION = packageJson.version;
const HMO_LOGOS = {
  [HMO.LEUMIT]: leumitLogo,
  [HMO.MACCABI]: defaultLogo,
  [HMO.CLALIT]: defaultLogo,
  [HMO.MEUCHEDET]: defaultLogo,
  [HMO.CLINICAL_TRIAL]: defaultLogo,
};

const isReactNativeWebView = typeof (window as any).ReactNativeWebView !== "undefined";

const Header = ({ currentStep }: HeaderProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const testType = useAppSelector(getTestTypeValue);
  const currentHmo = detectHmo();
  const testTypeLocaleKey = getTestTypeLocaleKey(testType);
  const isLeumit = currentHmo === HMO.LEUMIT;

  const useStyles = makeStyles()((theme) => styles(theme));
  const { classes } = useStyles();
  const isModelsLoaded = useAppSelector((state) => state.imageRecognition.isModelsLoaded);
  const usesSelfReport = isSelfReportHmo(currentHmo);
  const brandLogo = HMO_LOGOS[currentHmo] || defaultLogo;

  const hmoLogoKey = currentHmo ? `common:hmo:${currentHmo}:logo` : null;
  const hmoNameKey = currentHmo ? `common:hmo:${currentHmo}:name` : null;

  const hmoLogo = useMemo(() => {
    if (!hmoLogoKey) return null;
    const logo = t(hmoLogoKey, { defaultValue: null });
    return logo !== hmoLogoKey ? logo : null;
  }, [hmoLogoKey, t]);

  const hmoName = useMemo(() => {
    if (!hmoNameKey) return null;
    const name = t(hmoNameKey, { defaultValue: null });
    return name !== hmoNameKey ? name : null;
  }, [hmoNameKey, t]);

  const StepsHeaderContent: StepsHeaderContent = useMemo(
    () => {
      const titleKey = isLeumit ? `header:step1:titleLeumit` : `header:step1:title`;
      return({
        0: {
          title: `${testTypeLocaleKey}:${titleKey}`,
          description: [
            `${testTypeLocaleKey}:header:step1:description1`,
            `${testTypeLocaleKey}:header:step1:description2`,
          ],
          descriptionType: "TEXT",
          logo: brandLogo,
        },
        1: {
          title: `${testTypeLocaleKey}:header:step2:title`,
          description: [`${testTypeLocaleKey}:header:step2:description`],
          descriptionType: "CONTAINER",
          logo: brandLogo,
        },
        2: {
          title: `${testTypeLocaleKey}:header:step3:title`,
          descriptionType: "STEPS",
          stepWidgetProgress: 1,
          logo: brandLogo,
        },
        3: {
          title: `${testTypeLocaleKey}:header:step3:title`,
          description: [""],
          descriptionType: "STEPS",
          stepWidgetProgress: 2,
          logo: brandLogo,
        },
        4: {
          title: `${testTypeLocaleKey}:header:step3:title`,
          description: [""],
          descriptionType: "STEPS",
          stepWidgetProgress: 3,
          logo: brandLogo,
        },
        5: {
          title: usesSelfReport
            ? "SelfReportScreen:title"
            : `${testTypeLocaleKey}:header:step4:title`,
          descriptionType: "LARGE_TEXT",
          logo: brandLogo,
        },
        6: {
          descriptionType: "SHORT",
          logo: brandLogo,
        },
      });
    },
    [testTypeLocaleKey, usesSelfReport, brandLogo]
  );

  const stepContent = StepsHeaderContent[currentStep];
  const modelsIndicatorColor = isModelsLoaded
    ? theme.palette.extraColors?.modelsIndicator ?? theme.palette.success.main
    : theme.palette.error.main;
  const showHmoSection = !isLeumit && currentStep === 0 && Boolean(hmoLogo || hmoName);
  const useCompactTopSpacing = !isLeumit && currentStep === 0 && Boolean(hmoLogo);
  const pageTitleMarginTop = isLeumit && currentStep === 5 ? theme.spacing.spacing12 : 0;

  let titleSpacingClass: string | undefined = classes.titleWithMargin;
  if (isLeumit) {
    titleSpacingClass = undefined;
  } else if (useCompactTopSpacing) {
    titleSpacingClass = classes.noPaddingTop;
  }

  let descriptionSpacingClass: string = classes.descriptionWithMargin;
  if (isLeumit) {
    descriptionSpacingClass = classes.descriptionLeumit;
  } else if (useCompactTopSpacing) {
    descriptionSpacingClass = classes.noPaddingTop;
  }

  const renderLanguageControls = () => (
    <div className={classes.languageWrapper} data-test-id="header-language-controls">
      <div
        className={classes.loadingCircle}
        style={{ background: modelsIndicatorColor }}
        data-test-id="header-models-indicator"
      />
      <SelectLanguage />
    </div>
  );

  const renderStepContent = () => (
    <>
      {stepContent.title && (
        <div
          className={classNames(
            titleSpacingClass,
            stepContent.descriptionType === "LARGE_TEXT"
              ? classes.largeTitle
              : classes.title,
            isLeumit && classes.titleLeumit
          )}
          data-test-id="header-step-title"
        >
          {t(stepContent.title)}
        </div>
      )}
      <div className={classNames(descriptionSpacingClass, classes.descriptionSection)}>
        {stepContent.descriptionType === "TEXT" && (
          <div
            className={classNames(
              classes.descriptionBoxText,
              isLeumit && classes.descriptionBoxTextLeumit
            )}
          >
            {stepContent.description?.map((description) => (
              <div key={description} className={classes.description}>
                {t(description)}
              </div>
            ))}
          </div>
        )}
        {stepContent.descriptionType === "CONTAINER" && (
          <div
            className={classNames(
              classes.descriptionBoxContainer,
              isLeumit && classes.descriptionBoxContainerLeumit
            )}
            data-test-id="header-description-box"
          >
            {isLeumit && (
              <AttentionIcon
                className={classes.descriptionBoxAlertIcon}
                aria-hidden
              />
            )}
            <span className={isLeumit ? classes.descriptionBoxAlertText : undefined}>
              {t(stepContent.description || "")}
            </span>
          </div>
        )}
        {stepContent.descriptionType === "STEPS" && (
          <div className={isLeumit ? classes.stepsBoxLeumit : classes.stepsBoxDefault}>
            <ProgressWidget currentProgressStep={stepContent.stepWidgetProgress} />
          </div>
        )}
      </div>
    </>
  );

  return (
    <Card style={{ width: "100%", boxShadow: "none" }} elevation={0}>
      <div
        className={classNames(
          stepContent.descriptionType === "SHORT"
            ? classes.headerContainer_SHORT
            : classes.headerContainer,
          classes.headerContainerBG,
          isLeumit && classes.headerContainerLeumit
        )}
        data-test-id={isLeumit ? "header-leumit" : "header-default"}
      >
        {isLeumit ? null : <div className={classes.versionText}>{VERSION}</div>}

        {isLeumit ? (
          <>
            {isReactNativeWebView ? null :
              <div className={classNames(classes.logoRow, { [classes.logoRowLeumit]: isLeumit })} data-test-id="header-top-row">
                <MDBox
                  component="img"
                  className={classNames(classes.brandLogo, classes.brandLogoLeumit)}
                  src={brandLogo}
                  alt="Brand"
                />
                <div className={classes.userSection} data-test-id="header-user-section">
                  {/* {MOCK_USER.avatarSrc ? <MDBox
                    component="img"
                    className={classes.userAvatar}
                    src={MOCK_USER.avatarSrc}
                    alt={MOCK_USER.name}
                  /> : null}
                  {MOCK_USER.name ? <span className={classes.userName}>{MOCK_USER.name}</span> : null} */}
                </div>
              </div>
            }
            <div className={classes.languageRow} data-test-id="header-middle-row">
              {renderLanguageControls()}
            </div>
            {stepContent.descriptionType !== "SHORT" && (
              <div className={classes.stepContentLeumit} style={{ marginTop: pageTitleMarginTop }} data-test-id="header-bottom-row">
                {renderStepContent()}
              </div>
            )}
          </>
        ) : (
          <>
            <div className={classes.logoRow}>
              <MDBox
                component="img"
                className={classes.brandLogo}
                src={brandLogo}
                alt="Brand"
              />
              {renderLanguageControls()}
            </div>
            {showHmoSection && (
              <div className={classes.hmoSection}>
                {hmoName && <div className={classes.hmoName}>{hmoName}</div>}
              </div>
            )}
            {renderStepContent()}
          </>
        )}
      </div>
    </Card>
  );
};

export default Header;
